import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { cn } from '../lib/utils'
import SignaturePad from '../components/SignaturePad'

// Public, mobile-first patient intake wizard. Rendered on an UNAUTHENTICATED route
// (IntakesController < PublicController) — the patient opens it from the tokenised
// WhatsApp link. It renders dynamically from each FormTemplate's `schema` JSON, so
// adding/editing forms is a seed change, not a frontend change.
//
// Print-and-sign: nothing is signed here. On submit the answers are filed; reception
// prints the pre-filled pack (IntakePdf) and the patient signs + initials by hand.

const TRUTHY = [true, 'true', '1', 'yes', 'on']
const truthy = (v) => TRUTHY.includes(v)

// Is a field visible given the current answers? (reveal_when conditional logic, plus the
// `esign_only` gate: the per-clause ticks and the CPA s49 initials belong to the ELECTRONIC route.
// On the print-and-sign route the patient initials each clause on the printout, so rendering them
// would be twelve taps that buy nothing — and the stored intro never mentions them.)
function isVisible(field, values, esign = true) {
  if (field.esign_only && !esign) return false
  const rule = field.reveal_when
  if (!rule) return true
  const expected = rule.equals
  const answer = values[rule.field]
  const actual = typeof expected === 'boolean' ? truthy(answer) : answer
  return actual === expected
}

// Validate a 13-digit South African ID number: real YYMMDD date + Luhn check digit.
// Fields carrying validate: "sa_id" must pass this before the patient can continue.
function isValidSAID(value) {
  const s = String(value || '').replace(/\s/g, '')
  if (!/^\d{13}$/.test(s)) return false
  const mm = +s.slice(2, 4)
  const dd = +s.slice(4, 6)
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return false
  let sum = 0
  let alt = false
  for (let i = s.length - 1; i >= 0; i--) {
    let n = +s[i]
    if (alt) { n *= 2; if (n > 9) n -= 9 }
    sum += n
    alt = !alt
  }
  return sum % 10 === 0
}

// Validate a typed date of birth (stored "YYYY-MM-DD"). Empty is allowed (optional);
// a partially-filled or impossible date is rejected.
function isValidDob(value) {
  const [y, m, d] = String(value || '').split('-')
  if (!y && !m && !d) return true
  if (!(y && m && d)) return false
  const yr = +y, mo = +m, da = +d
  if (y.length !== 4 || yr < 1900 || yr > new Date().getFullYear()) return false
  const dt = new Date(yr, mo - 1, da)
  return dt.getFullYear() === yr && dt.getMonth() === mo - 1 && dt.getDate() === da
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-brand-surface text-brand-ink">
      <div className="mx-auto w-full max-w-xl px-4 py-6">{children}</div>
    </div>
  )
}

function Centered({ emoji, title, body }) {
  return (
    <Shell>
      <div className="mt-16 text-center">
        <div className="text-5xl mb-4">{emoji}</div>
        <h1 className="text-2xl font-semibold mb-3">{title}</h1>
        <p className="text-sm text-brand-muted">{body}</p>
      </div>
    </Shell>
  )
}

export default function PublicIntake(props) {
  const { token, invalid, completed, patient, practice, privacy_notice = [], templates = [], draft_data = null, last_step = null, esign = false, reason_step = false,
          otp_required = false, otp_masked_phone = null, otp_masked_email = null } = props

  if (invalid || !token) {
    return (
      <Centered
        emoji="🔒"
        title="This link is no longer valid"
        body="Your intake link may have expired (links are valid for 14 days). Please contact the practice and we'll send you a fresh one."
      />
    )
  }

  if (completed) {
    return (
      <>
        <IntakeConversionSeed conversion={props.conversion} token={token} />
        <Centered
          emoji="✅"
          title="All done — thank you!"
          body="We've received your forms. There's nothing more to do now; we'll print them for you to sign when you arrive for your visit."
        />
      </>
    )
  }

  return <Wizard token={token} patient={patient} practice={practice} privacyNotice={privacy_notice} templates={templates} draftData={draft_data} lastStep={last_step} esign={esign} reasonStep={reason_step}
                 otpRequired={otp_required} otpMaskedPhone={otp_masked_phone} otpMaskedEmail={otp_masked_email} />
}

// Enhanced-Conversions seed for Google Ads, fired ONCE when a patient finishes their intake.
//
// 🚨 DELIBERATELY NOT GOOGLE TAG MANAGER. Loading container GTM-5C52ZSHK here would drag EIGHT
// All-Pages tags onto this subdomain — including MICROSOFT CLARITY SESSION RECORDING — onto a form
// containing medical history, ID numbers and a signature. That is a POPIA breach, not a tracking
// decision. This loads gtag directly and fires exactly ONE conversion, with exactly TWO fields.
//
// It reuses the SAME conversion action and label the WordPress contact form already uses
// (AW-303686715/06lQCNirncMcELvI55AB → "Website Lead Form", id 7657182680), so no new conversion
// action, no container change, and no GTM publish is required.
//
// Google's gtag hashes email/phone in the browser (SHA-256) before anything leaves the device.
// The `_gcl_aw` cookie is scoped to .chalitaleroux.co.za, so a click from the main site is
// readable here and Google can tie this conversion back to it — verified in a real browser.
function IntakeConversionSeed({ conversion, token }) {
  React.useEffect(() => {
    if (!conversion || !conversion.send_to) return
    // Fire once per intake, ever — revisiting the "all done" page must not re-convert.
    const key = `drc_intake_conv_${token}`
    try { if (window.localStorage.getItem(key)) return } catch (e) { /* private mode: fire anyway */ }

    const run = () => {
      window.dataLayer = window.dataLayer || []
      function gtag() { window.dataLayer.push(arguments) }
      gtag('js', new Date())
      gtag('config', 'AW-303686715', { allow_enhanced_conversions: true })
      const ud = {}
      if (conversion.email) ud.email = conversion.email
      if (conversion.phone_e164) ud.phone_number = conversion.phone_e164
      gtag('set', 'user_data', ud)
      gtag('event', 'conversion', { send_to: conversion.send_to })
      try { window.localStorage.setItem(key, '1') } catch (e) { /* ignore */ }
    }

    if (window.gtag || document.querySelector('script[data-drc-gtag]')) { run(); return }
    const s = document.createElement('script')
    s.async = true
    s.src = 'https://www.googletagmanager.com/gtag/js?id=AW-303686715'
    s.setAttribute('data-drc-gtag', '1')
    s.onload = run
    document.head.appendChild(s)
  }, [conversion, token])

  return null
}

// The paper-wording rewrite used to live here as fixIntro(). It now runs SERVER-SIDE in
// PaperlessWording, because the same stored strings are rendered in two places — this wizard and
// the PDF the practice keeps — and only the intro was ever covered here. The consent intro and the
// REQUIRED acknowledgement tick-box ("...will sign and initial the printed form on arrival") were
// not, so a patient could sign electronically while ticking a box promising to sign on paper.
// One implementation, on the server, is what stops the screen and the record disagreeing.

// Attach the drawn stroke to every template in this intake, under the key IntakeProcessor
// already reads (`_signature_data`). It is stripped out of the stored answer blob and kept in
// the encrypted `signature_data` column instead — the stroke is biometric-ish personal
// information (POPIA ss26–27) and does not belong in a plain JSON answer set.
function withStroke(answers, templates, stroke) {
  const out = { ...answers }
  templates.forEach((t) => { out[t.key] = { ...(out[t.key] || {}), _signature_data: stroke } })
  return out
}

const CAPACITIES = [
  { value: 'self',        label: 'I am the patient' },
  { value: 'parent',      label: 'I am the patient’s parent' },
  { value: 'guardian',    label: 'I am the patient’s legal guardian' },
  { value: 'care_giver',  label: 'I am the patient’s care-giver' },
]

// The signature step. What it collects is fixed by 02-esignature-standard S-3: a typed full
// name (the operative signature), the capacity the person signs in, and — where they are not
// the patient — how they are related. Everything else is stamped server-side.
// The one-time-code block (02-esignature-standard S-5, T2 remote signing). Sits INSIDE the sign
// step rather than being a step of its own: the code confirms this signature, so it belongs beside
// the thing it is confirming, and a patient who has already typed their name is not made to
// navigate away and back.
//
// The patient is told WHERE the code is going before they ask for it — a code arriving at an old
// number is then something they can tell us about instead of a dead end.
function OtpBlock({ maskedPhone, maskedEmail, code, setCode, sent, sentTo, busy, note, error, cooldown, onSend, highlighted }) {
  const destinations = [maskedPhone && `WhatsApp ${maskedPhone}`, maskedEmail && maskedEmail].filter(Boolean)

  return (
    <div id={highlighted ? 'intake-highlight' : undefined}
         className={`rounded-xl border p-4 ${highlighted ? 'border-red-400 ring-2 ring-red-200' : 'border-slate-300 bg-slate-50'}`}>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        Confirm it's you <span className="text-red-500">*</span>
      </label>
      <p className="mb-3 text-xs leading-relaxed text-slate-600">
        These forms include your consent to treatment, so we send a 6-digit code to the contact
        details we already have for you{destinations.length ? <> — <span className="font-semibold text-slate-800">{destinations.join(' and ')}</span></> : ''}.
        {destinations.length > 1 ? ' Use whichever arrives first.' : ''}
      </p>

      {!sent ? (
        <button type="button" onClick={onSend} disabled={busy}
          className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60">
          {busy ? 'Sending…' : 'Send my code'}
        </button>
      ) : (
        <>
          <input
            type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="w-full rounded-xl border border-slate-300 p-3 text-center text-2xl font-semibold tracking-[0.4em]"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">{note}</p>
            <button type="button" onClick={onSend} disabled={busy || cooldown > 0}
              className="shrink-0 text-xs font-semibold text-slate-700 underline disabled:no-underline disabled:opacity-50">
              {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
            </button>
          </div>
        </>
      )}

      {error && <p className="mt-2 text-xs font-medium text-red-600" role="alert">{error}</p>}
      {sent && !error && sentTo && (
        <p className="mt-2 text-xs text-emerald-700">✓ Code sent to your {sentTo}. It expires in 10 minutes.</p>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
        We will never phone or message you to ask for this code.
      </p>
    </div>
  )
}

function SignStep({ sig, setSig, stroke, setStroke, practice, highlighted, otp }) {
  // setStroke receives { dataUrl, metrics } from the shared pad
  const set = (k, v) => setSig((p) => ({ ...p, [k]: v }))
  const isSelf = sig.signer_capacity === 'self'

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Sign your forms</h2>
        <p className="mt-1 text-sm text-slate-600">
          This replaces signing on paper when you arrive. Signing confirms that the answers you
          have given are true and complete as far as you know, and that you have read the privacy
          notice at the start.
        </p>
      </div>

      {/* NHA s6(2) — informed in a language the patient understands. Recorded, not assumed:
          every signature used to be stamped "en" whatever was actually spoken. */}
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Which language would you like this explained in?</label>
        <div className="grid grid-cols-2 gap-2">
          {[{ v: 'en', l: 'English' }, { v: 'af', l: 'Afrikaans' }].map((o) => (
            <label key={o.v} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 ${sig.language === o.v ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}>
              <input type="radio" name="lang" checked={sig.language === o.v}
                onChange={() => set('language', o.v)} className="h-4 w-4" />
              <span className="text-sm text-slate-800">{o.l}</span>
            </label>
          ))}
        </div>
        {sig.language !== 'en' && (
          <p className="mt-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-900">
            Hierdie vorms is in Engels. Ons gaan dit in Afrikaans met u deurgaan wanneer u kom.
            <span className="mt-1 block text-amber-800">These forms are in English. We will go through them with you in Afrikaans when you arrive.</span>
          </p>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Who is signing? <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {CAPACITIES.map((c) => (
            <label key={c.value} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${sig.signer_capacity === c.value ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}>
              <input
                type="radio" name="capacity" value={c.value}
                checked={sig.signer_capacity === c.value}
                onChange={() => set('signer_capacity', c.value)}
                className="h-4 w-4"
              />
              <span className="text-sm text-slate-800">{c.label}</span>
            </label>
          ))}
        </div>
        {sig.signer_capacity === 'care_giver' && (
          <p className="mt-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800">
            A care-giver may consent to ordinary treatment, but a parent or legal guardian must
            consent to an operation. We may ask for a parent or guardian on the day.
          </p>
        )}
      </div>

      {!isSelf && (
        <div id={highlighted === '__relationship__' ? 'intake-highlight' : undefined}>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            How are you related to the patient? <span className="text-red-500">*</span>
          </label>
          <input
            type="text" value={sig.signer_relationship}
            onChange={(e) => set('signer_relationship', e.target.value)}
            placeholder="e.g. mother, father, legal guardian"
            className={`w-full rounded-xl border p-3 text-base ${highlighted === '__relationship__' ? 'border-red-400 ring-2 ring-red-200' : 'border-slate-300'}`}
          />
        </div>
      )}

      <div id={highlighted === '__signed_name__' ? 'intake-highlight' : undefined}>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Your full name <span className="text-red-500">*</span>
        </label>
        <input
          type="text" value={sig.signed_name} autoComplete="name"
          onChange={(e) => set('signed_name', e.target.value)}
          placeholder="First name and surname"
          className={`w-full rounded-xl border p-3 text-base ${highlighted === '__signed_name__' ? 'border-red-400 ring-2 ring-red-200' : 'border-slate-300'}`}
        />
        <p className="mt-1 text-xs text-slate-500">Typing your name here is what signs these forms.</p>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Your ID or passport number <span className="text-slate-400">(optional)</span>
        </label>
        <input
          type="text" value={sig.signer_id_number} inputMode="numeric"
          onChange={(e) => set('signer_id_number', e.target.value)}
          className="w-full rounded-xl border border-slate-300 p-3 text-base"
        />
        <p className="mt-1 text-xs text-slate-500">Helps us be sure we have the right person on the day.</p>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Signature <span className="text-red-500">*</span>
        </label>
        <SignaturePad onChange={setStroke} highlighted={highlighted === '__signature__'} />
      </div>

      {otp?.required && <OtpBlock {...otp} highlighted={highlighted === '__otp__'} />}

      <p className="rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
        {practice?.name || 'The practice'} records the date and time you signed, and keeps this
        signed copy in your file. You can ask us for a copy at any time.
      </p>
    </div>
  )
}

// Reason-for-visit step (Paul 2026-08-21). Patient's OWN WORDS (free text) + a structured visit type that
// lets reception route cosmetic/implant work to Dr Chalita + an optional pain/urgency question. All fields
// optional — it must never trap a patient. Values live under answers._visit_reason.
const VISIT_TYPE_OPTIONS = [
  { v: 'checkup', l: 'Check-up & clean' },
  { v: 'pain', l: 'Pain or an urgent problem' },
  { v: 'broken', l: 'Broken tooth / lost filling or crown' },
  { v: 'cosmetic', l: 'Cosmetic / smile makeover (whitening, veneers, aligners)' },
  { v: 'implants', l: 'Implants / changing my smile' },
  { v: 'rootcanal_extraction', l: 'Root canal or extraction' },
  { v: 'unsure', l: 'Not sure / something else' },
]
function ReasonStep({ values, setValue }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Reason for your visit</h2>
        <p className="mt-1 text-sm text-slate-600">This helps us prepare and make sure you see the right dentist. A short note is perfect.</p>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          What is the reason for your visit? What would you like to see the dentist about?
        </label>
        <textarea
          rows={3}
          value={values.reason || ''}
          onChange={(e) => setValue('reason', e.target.value)}
          maxLength={600}
          placeholder="e.g. pain in an upper left tooth · I'd like to improve how my front teeth look · a broken filling · a check-up and clean"
          className="w-full rounded-xl border border-slate-300 p-3 text-base"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">What best describes your visit?</label>
        <div className="space-y-2">
          {VISIT_TYPE_OPTIONS.map((o) => (
            <label key={o.v} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${values.visit_type === o.v ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}>
              <input type="radio" name="visit_type" value={o.v} checked={values.visit_type === o.v}
                     onChange={() => setValue('visit_type', o.v)} className="h-4 w-4" />
              <span className="text-sm text-slate-800">{o.l}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Are you currently having any pain or discomfort?</label>
        <div className="flex gap-2">
          {[ [ 'No', false ], [ 'Yes', true ] ].map(([ lab, val ]) => (
            <button key={lab} type="button" onClick={() => setValue('in_pain', val)}
                    className={`flex-1 rounded-xl border p-3 text-sm font-medium ${values.in_pain === val ? 'border-slate-900 bg-slate-50 text-slate-900' : 'border-slate-200 text-slate-700'}`}>
              {lab}
            </button>
          ))}
        </div>
        {values.in_pain === true && (
          <input
            type="text"
            value={values.pain_duration || ''}
            onChange={(e) => setValue('pain_duration', e.target.value)}
            maxLength={80}
            placeholder="How long has it been bothering you? e.g. 3 days"
            className="mt-2 w-full rounded-xl border border-slate-300 p-3 text-base"
          />
        )}
      </div>
    </div>
  )
}

function Wizard({ token, patient, practice, privacyNotice, templates, draftData, lastStep, esign = false, reasonStep = false,
                  otpRequired = false, otpMaskedPhone = null, otpMaskedEmail = null }) {
  // The shared /intake/new link has no patient to key a draft to — never autosave it (the server
  // no-ops it anyway). Everything else is a per-patient tokenised link that autosaves + resumes.
  const isGeneric = token === 'new'

  // Flatten the templates into wizard steps FIRST so the resume step can be clamped to a valid index.
  // The signature step is LAST — the patient signs once, for the whole intake, after they have seen
  // everything they are signing for (ECTA s13(3): the method must indicate their approval of the
  // information, which means the information must already be in front of them).
  const steps = useMemo(() => {
    const list = [{ kind: 'privacy' }]
    // Reason-for-visit (Paul 2026-08-21) — an early step, right after the privacy notice, so the patient
    // tells us in their own words why they're coming before the longer detail/health sections. Flag-gated.
    if (reasonStep) list.push({ kind: 'reason' })
    templates.forEach((t) => {
      const sections = (t.schema && t.schema.sections) || []
      sections.forEach((section, idx) => {
        list.push({
          kind: 'section',
          templateKey: t.key,
          templateName: t.name,
          // Already corrected server-side by PaperlessWording when e-sign is on for this patient.
          intro: idx === 0 ? t.schema.intro : null,
          section,
        })
      })
    })
    if (esign) list.push({ kind: 'sign' })
    return list
  }, [templates, esign, reasonStep])

  // RESUME (Glen Clark, Paul 2026-07-22): if the server sent a saved draft, hydrate the answers and
  // land the patient back on the step they left — no "resume?" prompt. If nothing was saved this is
  // identical to before: blank answers, step 0.
  const resumedAnswers = draftData && typeof draftData === 'object' ? draftData : null
  const resumedStep = Number.isInteger(lastStep)
    ? Math.min(Math.max(lastStep, 0), steps.length - 1)
    : 0

  // answers: { [templateKey]: { [fieldKey]: value } }
  const [answers, setAnswers] = useState(() => resumedAnswers || {})
  // If they resumed PAST the privacy step they must already have acknowledged it — pre-tick so a
  // "Back" to step 0 doesn't re-trap them, and so the resumed step never fails the privacy gate.
  const [privacyAck, setPrivacyAck] = useState(() => resumedStep > 0)
  const [stepIndex, setStepIndex] = useState(resumedStep)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)
  // The required field (by key) that blocked Submit and must be scrolled-to + highlighted, plus a
  // nonce so repeated taps re-trigger the scroll even when the same field is still missing.
  const [highlightKey, setHighlightKey] = useState(null)
  const [highlightTick, setHighlightTick] = useState(0)
  // Honeypot: hidden from humans, bots fill it. Submitted as `website`; the server silently
  // drops any submission where it's non-empty (see IntakesController#create_generic).
  const [hp, setHp] = useState('')
  // The signature block. Only the SIGNER'S OWN input lives here — the time, IP, versions and
  // hash are stamped by the server and are never sent from this page (S-4.5). The drawn stroke
  // is kept out of the autosaved draft: a half-drawn signature must never be resurrected and
  // presented as a signature the patient gave.
  const [sig, setSig] = useState({ signed_name: '', signer_id_number: '', signer_capacity: 'self', signer_relationship: '', language: 'en', interpreter_name: '' })
  const [sigStroke, setSigStroke] = useState('')
  // The one-time code. DELIBERATELY NOT AUTOSAVED — a code resurrected from a draft would be an
  // expired code presented as a fresh one, and the stroke is kept out of the draft for the same
  // reason. `otpVerified` does not exist here on purpose: the server verifies on the submit, so
  // there is no client-held boolean for a crafted POST to assert.
  const [otpCode, setOtpCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otpSentTo, setOtpSentTo] = useState('')
  const [otpBusy, setOtpBusy] = useState(false)
  const [otpError, setOtpError] = useState(null)
  const [otpCooldown, setOtpCooldown] = useState(0)
  const [sigMetrics, setSigMetrics] = useState(null)

  const step = steps[stepIndex]
  const isLast = stepIndex === steps.length - 1
  const progress = Math.round(((stepIndex + 1) / steps.length) * 100)

  // Always-current step. A debounced field-autosave captures the step in its closure at edit time; if
  // the patient advances before it fires (< the debounce window), its late commit must NOT record an
  // earlier step and send resume backward. Reading this ref at fire time tags the step they're actually
  // on, so resume always lands where they left off.
  const stepRef = useRef(stepIndex)
  stepRef.current = stepIndex

  const valuesFor = (key) => answers[key] || {}
  const setValue = (templateKey, fieldKey, value) => {
    setAnswers((prev) => ({ ...prev, [templateKey]: { ...(prev[templateKey] || {}), [fieldKey]: value } }))
  }

  // Best-effort autosave — fire-and-forget; a flaky/offline network must NEVER block the patient. The
  // body is EXACTLY the answers object we'd submit, under `data`, plus the 0-based step (backend
  // contract). Autosave never completes the intake (see IntakesController#autosave), so it's safe to
  // call freely. Skipped on the shared /intake/new link (no patient to key a draft to).
  const sendAutosave = useCallback((data, step) => {
    if (isGeneric) return
    // Nothing entered yet (e.g. advancing straight past the privacy step) → don't POST an empty draft.
    // The backend requires a non-empty `data` (params.require) and would 422 it; there's nothing to
    // resume to anyway. The next real edit autosaves with content.
    const hasAnswers = data && typeof data === 'object' &&
      Object.values(data).some((s) => s && typeof s === 'object' && Object.keys(s).length > 0)
    if (!hasAnswers) return
    try {
      const csrf = (typeof document !== 'undefined' &&
        document.querySelector('meta[name="csrf-token"]')?.content) || ''
      fetch(`/intake/${token}/autosave`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrf,
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ data, step }),
        credentials: 'same-origin',
        keepalive: true, // let the last save land even as the tab is closing
      }).catch(() => { /* offline / best-effort — swallow */ })
    } catch (_) { /* never surface an autosave failure to the patient */ }
  }, [isGeneric, token])

  // Debounced autosave on field edits (~800ms after the last keystroke). Skips the hydration render so
  // resuming doesn't immediately re-post the data we were just handed.
  const skipAnswersAutosave = useRef(true)
  useEffect(() => {
    if (isGeneric) return
    if (skipAnswersAutosave.current) { skipAnswersAutosave.current = false; return }
    const t = setTimeout(() => sendAutosave(answers, stepRef.current), 800)
    return () => clearTimeout(t)
  }, [answers]) // eslint-disable-line react-hooks/exhaustive-deps

  // Immediate autosave whenever the step changes, so a close-and-reopen lands on the right step.
  const skipStepAutosave = useRef(true)
  useEffect(() => {
    if (isGeneric) return
    if (skipStepAutosave.current) { skipStepAutosave.current = false; return }
    sendAutosave(answers, stepIndex)
  }, [stepIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  // The moment the patient edits anything, clear a stale "please tick this" highlight.
  useEffect(() => { setHighlightKey(null) }, [answers, privacyAck])

  // Scroll the blocking field into view each time Submit is blocked (the nonce forces a re-scroll even
  // if the same field is still the culprit).
  useEffect(() => {
    if (!highlightKey) return
    const el = typeof document !== 'undefined' && document.getElementById('intake-highlight')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // Returns an error message if the current section isn't ready to advance, else null.
  // Checks required visible fields AND field-level validators (e.g. validate: "sa_id").
  // Only a MISSING required field blocks "Next" — and the message NAMES the field so the patient knows
  // exactly what to fix. Format quirks (ID checksum / odd date) are NON-blocking warnings (see
  // stepWarning) so the form never silently traps a patient (Diphetlho Masemola 2026-06-29: a strict
  // SA-ID Luhn block stuck her at step 2 with a generic error → she could never reach Submit).
  // Resend cooldown, mirroring SignatureOtp::RESEND_WAIT. Client-side courtesy only — the server
  // enforces the real limit and returns 429, which is what actually protects the patient's phone.
  useEffect(() => {
    if (otpCooldown <= 0) return undefined
    const t = setTimeout(() => setOtpCooldown((n) => n - 1), 1000)
    return () => clearTimeout(t)
  }, [otpCooldown])

  const sendOtp = async () => {
    setOtpBusy(true)
    setOtpError(null)
    try {
      const csrf = (typeof document !== 'undefined' &&
        document.querySelector('meta[name="csrf-token"]')?.content) || ''
      const res = await fetch(`/intake/${token}/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({}),
        credentials: 'same-origin',
      })
      const body = await res.json().catch(() => ({}))
      if (body && body.ok) {
        setOtpSent(true)
        setOtpSentTo(body.sent_to || '')
        setOtpCooldown(45)
      } else {
        // A failed send must still leave the patient somewhere to type a code they may have
        // received on a previous tap — never wipe the input or hide the field.
        setOtpError((body && body.error) || 'Sorry — we couldn’t send your code. Please try again shortly.')
      }
    } catch (e) {
      setOtpError('Sorry — we couldn’t reach the server. Please check your connection and tap Send again.')
    } finally {
      setOtpBusy(false)
    }
  }

  const stepError = () => {
    if (step.kind === 'privacy') return privacyAck ? null : 'Please confirm you have read the privacy notice.'
    if (step.kind === 'sign') {
      if (sig.signed_name.trim().length < 3) return 'Please type your full name — that is what signs these forms.'
      if (!sigStroke) return 'Please sign your name in the box — a tap or a small mark is not a signature.'
      if (sig.signer_capacity !== 'self' && !sig.signer_relationship.trim()) {
        return 'Please say how you are related to the patient (e.g. mother, father, legal guardian).'
      }
      if (otpRequired && !otpSent) return 'Please tap “Send my code”, then enter the 6-digit code we send you.'
      if (otpRequired && otpCode.trim().length !== 6) return 'Please enter the 6-digit code we sent you.'
      return null
    }
    if (step.kind === 'reason') return null // reason-for-visit step: all fields optional, never blocks Next
    const values = valuesFor(step.templateKey)
    for (const f of step.section.fields) {
      if (!isVisible(f, values, esign)) continue
      const v = values[f.key]
      const empty = f.type === 'yesno' ? !(v === true || v === false)
        : f.type === 'checkbox' ? v !== true
        : (v === undefined || String(v).trim() === '')
      if (f.required && empty) return `Please complete: ${f.label || 'the required field'}.`
      // Blocking, not a nudge: this is the acknowledgement the clause exists for.
      if (f.type === 'initials' && !empty && String(v).trim().length < 2) {
        return `Please type at least two initials for: ${f.label}.`
      }
    }
    return null
  }

  // Gentle, NON-blocking quality nudge for a filled-but-odd ID / date. Reception verifies on arrival
  // (the pack prints for signing), so a quirk never costs the practice a booking.
  const stepWarning = () => {
    if (step.kind === 'privacy' || step.kind === 'sign' || step.kind === 'reason') return null
    const values = valuesFor(step.templateKey)
    for (const f of step.section.fields) {
      if (!isVisible(f, values, esign)) continue
      const v = values[f.key]
      const empty = v === undefined || String(v).trim() === ''
      if (!empty && f.validate === 'sa_id' && !isValidSAID(v)) return 'That doesn’t look like a valid 13-digit SA ID — please double-check, or choose Passport above. You can still continue.'
      if (!empty && f.type === 'dob' && !isValidDob(v)) return 'Please double-check the date of birth — you can still continue.'
    }
    return null
  }

  // The key of the first required field blocking the current step (or the sentinel for the privacy
  // acknowledgement), so goNext can scroll-to + highlight EXACTLY what's missing — most importantly the
  // required consent checkbox on the final step, so "I tapped Submit" can't be mistaken for "submitted".
  const firstMissingKey = () => {
    if (step.kind === 'privacy') return privacyAck ? null : '__privacy__'
    if (step.kind === 'reason') return null
    if (step.kind === 'sign') {
      if (sig.signed_name.trim().length < 3) return '__signed_name__'
      if (!sigStroke) return '__signature__'
      if (sig.signer_capacity !== 'self' && !sig.signer_relationship.trim()) return '__relationship__'
      if (otpRequired && otpCode.trim().length !== 6) return '__otp__'
      return null
    }
    const values = valuesFor(step.templateKey)
    for (const f of step.section.fields) {
      if (!isVisible(f, values, esign)) continue
      const v = values[f.key]
      const empty = f.type === 'yesno' ? !(v === true || v === false)
        : f.type === 'checkbox' ? v !== true
        : (v === undefined || String(v).trim() === '')
      if (f.required && empty) return f.key
      if (f.type === 'initials' && !empty && String(v).trim().length < 2) return f.key
    }
    return null
  }

  const goNext = () => {
    const err = stepError()
    if (err) {
      setError(err)
      // Don't just show an error above a button the patient may never scroll to — take them to the
      // exact field that's blocking (e.g. the required consent checkbox on the final step) and
      // highlight it. No submit fetch fires while the step is blocked.
      const missing = firstMissingKey()
      if (missing) {
        setHighlightKey(missing)
        setHighlightTick((n) => n + 1)
      }
      return
    }
    setError(null)
    setHighlightKey(null)
    if (isLast) return submit()
    setStepIndex((i) => i + 1)
    window.scrollTo(0, 0)
  }

  const goBack = () => {
    setError(null)
    setHighlightKey(null)
    setStepIndex((i) => Math.max(0, i - 1))
    window.scrollTo(0, 0)
  }

  // Submit via a plain fetch, NOT Inertia's router.patch. router.patch is gated by Inertia's
  // asset-version check: if the app is redeployed while a patient has the form open, the version
  // mismatches and Inertia does a full-page reload (409) INSTEAD of sending the PATCH — the form
  // silently "does not submit" (real patient report, 2026-06-24). A bare fetch is immune to that,
  // so this public, long-lived form survives every deploy. The server returns a 303 redirect on
  // success (fetch follows it → res.ok); we then show the done screen.
  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const csrf = (typeof document !== 'undefined' &&
        document.querySelector('meta[name="csrf-token"]')?.content) || ''
      const res = await fetch(`/intake/${token}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, text/html',
          'X-CSRF-Token': csrf,
          'X-Requested-With': 'XMLHttpRequest',
        },
        // The drawn stroke rides inside the answers under `_signature_data` (where
        // IntakeProcessor#signature_for already looks for it and strips it out of the stored
        // answer blob); the legally operative typed block goes in `signature`.
        body: JSON.stringify({
          answers: esign && sigStroke ? withStroke(answers, templates, sigStroke) : answers,
          signature: esign ? sig : undefined,
          signature_metrics: esign ? sigMetrics : undefined,
          // The server re-checks this against the digest it stored and BURNS it, so a replayed
          // submit fails even with a captured body.
          otp_code: otpRequired ? otpCode.trim() : undefined,
          website: hp,
        }),
        credentials: 'same-origin',
      })
      if (res.ok || res.redirected || (res.status >= 200 && res.status < 400)) {
        setDone(true)
        window.scrollTo(0, 0)
      } else {
        // The server returns a clean JSON { error } on a rejected submit (never a raw error page) —
        // show it so the patient knows what to fix, else a friendly fallback.
        let msg = 'Something went wrong saving your form. Please try again, or reply on WhatsApp and we’ll help.'
        let otpState = null
        try {
          const body = await res.json()
          if (body && body.error) msg = body.error
          if (body && body.otp_state) otpState = body.otp_state
        } catch (_) { /* non-JSON */ }
        // A one-time code is one-time: whatever happened, the digits on screen are now stale.
        // "resend" additionally means the code itself is gone, so put the patient back to the
        // button that can actually get them a working one.
        if (otpRequired && otpState) {
          setOtpCode('')
          if (otpState === 'resend') { setOtpSent(false); setOtpSentTo(''); setOtpCooldown(0) }
        }
        setSubmitting(false)
        setError(msg)
      }
    } catch (e) {
      setSubmitting(false)
      setError('Sorry — we couldn’t reach the server. Please check your connection and tap Submit again.')
    }
  }

  if (done) {
    return (
      <Centered
        emoji="✅"
        title="All done — thank you!"
        body={esign
          ? 'We’ve received your signed forms and filed them in your record. There’s nothing to print or sign when you arrive — we’ll just check your ID at reception.'
          : "We've received your forms. There's nothing more to do now; we'll print them for you to sign when you arrive for your visit."}
      />
    )
  }

  return (
    <Shell>
      {/* Honeypot — visually hidden, off-screen; only bots fill it. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
        <label>Website
          <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
        </label>
      </div>
      {/* Header + progress */}
      <div className="mb-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary">
          {practice?.name || 'Dr Chalita le Roux Inc'}
        </p>
        <h1 className="text-xl font-semibold mt-1">Hi 👋</h1>
        {/* 🚨 SAY WHOSE FORM THIS IS (Paul 2026-07-20, Dullisear family). The greeting was hardcoded
            "Hi Patient" and never used the `patient` prop the server already sends, so every intake
            looked identical. Wanita Dullisear was sent a form for herself and one for her son Gabriel,
            on top of seven older links for her daughter Indigo whose forms were already completed —
            she kept opening one of those and reporting "it just says it's done already", with no way
            to tell the forms apart. A parent filling in several forms must be able to see, at a
            glance, which child this one belongs to. */}
        {patient?.first_name?.trim() && (
          <p className="mt-1 text-sm text-brand-muted">
            This form is for <span className="font-semibold text-brand-primary">{patient.first_name.trim()}</span>
          </p>
        )}
        <div className="mt-3 h-1.5 w-full rounded-full bg-brand-border">
          <div className="h-1.5 rounded-full bg-brand-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-1 text-xs text-brand-muted">Step {stepIndex + 1} of {steps.length}</p>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-brand-border">
        {step.kind === 'privacy' ? (
          <PrivacyStep notice={privacyNotice} ack={privacyAck} setAck={setPrivacyAck} highlighted={highlightKey === '__privacy__'} />
        ) : step.kind === 'reason' ? (
          <ReasonStep values={valuesFor('_visit_reason')} setValue={(k, v) => setValue('_visit_reason', k, v)} />
        ) : step.kind === 'sign' ? (
          <SignStep sig={sig} setSig={setSig} stroke={sigStroke}
                    setStroke={({ dataUrl, metrics }) => { setSigStroke(dataUrl); setSigMetrics(metrics) }}
                    practice={practice} highlighted={highlightKey}
                    otp={{
                      required: otpRequired, maskedPhone: otpMaskedPhone, maskedEmail: otpMaskedEmail,
                      code: otpCode, setCode: setOtpCode, sent: otpSent, sentTo: otpSentTo,
                      busy: otpBusy, error: otpError, cooldown: otpCooldown, onSend: sendOtp,
                      note: 'Enter the 6-digit code we sent you.',
                    }} />
        ) : (
          <SectionStep step={step} values={valuesFor(step.templateKey)} setValue={setValue} highlightKey={highlightKey} esign={esign} />
        )}
      </div>

      {/* Spacer so the fixed action bar never covers the last field of a step. */}
      <div aria-hidden="true" className="h-28" />

      {/* Sticky action bar — always on screen so the advance button is never trapped below the fold on
          the long Patient-details / Health / Consent steps (mobile). Safe-area padding keeps it clear of
          the iOS home indicator. Any error/warning rides in the bar so it's always visible too. */}
      <div
        data-testid="intake-sticky-bar"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-brand-border bg-white/95 backdrop-blur"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto w-full max-w-xl px-4 py-3">
          {error && <p className="mb-2 text-sm text-brand-danger" role="alert">{error}</p>}
          {!error && stepWarning() && <p className="mb-2 text-sm text-amber-600">⚠ {stepWarning()}</p>}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={stepIndex === 0 || submitting}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-brand-ink-soft disabled:opacity-0"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={submitting}
              className="rounded-xl bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-60"
            >
              {submitting ? 'Saving…' : isLast ? 'Submit' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </Shell>
  )
}

function PrivacyStep({ notice, ack, setAck, highlighted }) {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-1">Before we start — your privacy</h2>
      <p className="text-sm text-brand-muted mb-4">Please read how we look after your information.</p>
      <div className="max-h-72 overflow-y-auto rounded-xl bg-brand-surface p-4 text-sm space-y-3">
        {notice.map((s) => (
          <div key={s.title}>
            <p className="font-semibold text-brand-ink">{s.title}</p>
            <p className="text-brand-ink-soft">{s.body}</p>
          </div>
        ))}
      </div>
      <label
        id={highlighted ? 'intake-highlight' : undefined}
        className={cn(
          'mt-4 flex items-start gap-3 rounded-xl text-sm scroll-mt-24',
          highlighted && 'p-2 ring-2 ring-brand-danger ring-offset-2 animate-pulse'
        )}
      >
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-5 w-5 accent-[var(--brand-primary)]" />
        <span>I have read the privacy notice above and understand how my information will be used and protected.</span>
      </label>
    </div>
  )
}

// Consecutive fields that carry the same `clause` number are ONE consent point - heading, body,
// its own tick, and (where the point limits what can be guaranteed) its own initials. Grouping them
// is not decoration: CPA s49 requires a risk-limiting term to be CONSPICUOUS and separately
// acknowledged, and a tick floating in a 30-field scroll is neither. A field with no `clause`
// renders exactly as it always did, so the other two forms are untouched.
function groupFields(fields) {
  const groups = []
  fields.forEach((f) => {
    const last = groups[groups.length - 1]
    if (f.clause && last && last.clause === f.clause) last.fields.push(f)
    else groups.push({ key: f.key, clause: f.clause || null, conspicuous: !!f.conspicuous, fields: [f] })
  })
  return groups
}

function SectionStep({ step, values, setValue, highlightKey, esign = true }) {
  const groups = groupFields(step.section.fields.filter((f) => isVisible(f, values, esign)))
  return (
    <div>
      {step.intro && <p className="mb-4 text-sm text-brand-muted">{step.intro}</p>}
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary">{step.templateName}</p>
      <h2 className="text-lg font-semibold mb-4">{step.section.title}</h2>
      <div className="space-y-4">
        {groups.map((group) => (
          <div
            key={group.key}
            className={cn(
              group.conspicuous && 'rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 space-y-3',
              group.clause && !group.conspicuous && 'rounded-xl border border-brand-border p-3 space-y-2'
            )}
          >
            {group.fields.map((field) => (
              <div
                key={field.key}
                id={highlightKey === field.key ? 'intake-highlight' : undefined}
                className={cn(
                  'scroll-mt-24 rounded-xl',
                  highlightKey === field.key && 'p-2 ring-2 ring-brand-danger ring-offset-2 animate-pulse'
                )}
              >
                <Field field={field} value={values[field.key]} onChange={(v) => setValue(step.templateKey, field.key, v)} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function Field({ field, value, onChange }) {
  const label = (
    <label className="block text-sm font-medium text-brand-ink">
      {field.label}{field.required && <span className="text-brand-danger"> *</span>}
    </label>
  )

  if (field.type === 'heading') {
    return <h3 className="pt-2 text-sm font-semibold text-brand-primary">{field.label}</h3>
  }
  if (field.type === 'statement') {
    return <p className="text-xs leading-relaxed text-brand-ink-soft">{field.label}</p>
  }

  if (field.type === 'yesno') {
    return (
      <div>
        {label}
        <div className="mt-1.5 flex gap-2">
          {[['Yes', true], ['No', false]].map(([text, val]) => (
            <button
              key={text}
              type="button"
              onClick={() => onChange(val)}
              className={cn(
                'flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold',
                value === val ? 'border-brand-primary bg-brand-primary text-white' : 'border-brand-border bg-white text-brand-ink-soft'
              )}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (field.type === 'checkbox') {
    return (
      <label className="flex items-start gap-3 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 accent-[var(--brand-primary)]" />
        <span>{field.label}{field.required && <span className="text-brand-danger"> *</span>}</span>
      </label>
    )
  }

  // Initials for a clause that limits what can be guaranteed (CPA s49). Deliberately SHORT - two
  // taps, not a second signature - because the pack is already signed with a full typed name, a
  // drawn stroke and a one-time code. Letters only, so it can never be mistaken for a field that
  // identifies the patient.
  if (field.type === 'initials') {
    return (
      <div>
        {label}
        {field.help && <p className="text-xs text-brand-muted">{field.help}</p>}
        <input
          type="text"
          value={value || ''}
          maxLength={4}
          autoComplete="off"
          autoCapitalize="characters"
          placeholder="e.g. PL"
          onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
          className="mt-1.5 w-28 rounded-xl border-2 border-amber-400 bg-white px-3 py-2.5 text-center text-base font-semibold tracking-widest"
        />
      </div>
    )
  }

  if (field.type === 'select') {
    return (
      <div>
        {label}
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-base"
        >
          <option value="" disabled>Select…</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    )
  }

  if (field.type === 'dob') {
    // Three TYPED fields (Day / Month / Year) — best practice for a birth date on mobile
    // (USWDS/NN-g): the patient types the year instead of scrolling a native picker back
    // decades. Stored as "YYYY-MM-DD" so the backend parses it as a date.
    const [yy = '', mm = '', dd = ''] = String(value || '').split('-')
    const digits = (s, n) => s.replace(/\D/g, '').slice(0, n)
    const set = (d, m, y) => {
      const parts = [y || '', m || '', d || '']
      onChange(parts.every((x) => x === '') ? '' : parts.join('-'))
    }
    const cell = 'mt-1 w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-base text-center'
    return (
      <div>
        {label}
        {field.help && <p className="text-xs text-brand-muted">{field.help}</p>}
        <div className="mt-1 grid grid-cols-3 gap-2">
          <label className="text-xs text-brand-muted">Day
            <input type="text" inputMode="numeric" autoComplete="bday-day" maxLength={2} placeholder="DD"
              value={dd} onChange={(e) => set(digits(e.target.value, 2), mm, yy)} className={cell} />
          </label>
          <label className="text-xs text-brand-muted">Month
            <input type="text" inputMode="numeric" autoComplete="bday-month" maxLength={2} placeholder="MM"
              value={mm} onChange={(e) => set(dd, digits(e.target.value, 2), yy)} className={cell} />
          </label>
          <label className="text-xs text-brand-muted">Year
            <input type="text" inputMode="numeric" autoComplete="bday-year" maxLength={4} placeholder="YYYY"
              value={yy} onChange={(e) => set(dd, mm, digits(e.target.value, 4))} className={cell} />
          </label>
        </div>
      </div>
    )
  }

  if (field.type === 'textarea') {
    return (
      <div>
        {label}
        {field.help && <p className="text-xs text-brand-muted">{field.help}</p>}
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mt-1.5 w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-base"
        />
      </div>
    )
  }

  // text / tel / email / date / id_number
  const inputType = { tel: 'tel', email: 'email', date: 'date' }[field.type] || 'text'
  const inputMode = field.type === 'id_number' ? 'numeric' : undefined
  return (
    <div>
      {label}
      {field.help && <p className="text-xs text-brand-muted">{field.help}</p>}
      <input
        type={inputType}
        inputMode={inputMode}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-base"
      />
    </div>
  )
}
